import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Separator } from "./separator";
import { cn } from "./utils";

/**
 * Joins related buttons into one control with shared edges (shadcn/ui button-group).
 * Pair it with `Button` using size `sm` or `md` so each button stays its natural width.
 */
const buttonGroupVariants = cva(
    "flex w-fit items-stretch [&>*]:focus-visible:relative [&>*]:focus-visible:z-10 [&>input]:flex-1 has-[>[data-slot=button-group]]:gap-2",
    {
        variants: {
            orientation: {
                horizontal:
                    "[&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:border-l-0 [&>*:not(:last-child)]:rounded-r-none",
                vertical:
                    "flex-col [&>*:not(:first-child)]:rounded-t-none [&>*:not(:first-child)]:border-t-0 [&>*:not(:last-child)]:rounded-b-none",
            },
        },
        defaultVariants: {
            orientation: "horizontal",
        },
    }
);

const ButtonGroup = React.forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithoutRef<"div"> & VariantProps<typeof buttonGroupVariants>
>(({ className, orientation, ...props }, ref) => (
    <div
        ref={ref}
        role="group"
        data-slot="button-group"
        data-orientation={orientation ?? "horizontal"}
        className={cn(buttonGroupVariants({ orientation }), className)}
        {...props}
    />
));
ButtonGroup.displayName = "ButtonGroup";

interface ButtonGroupTextProps extends React.ComponentPropsWithoutRef<"div"> {
    asChild?: boolean;
}

/** Static text that sits inside a group, such as "2 of 5" between Back and Next. */
const ButtonGroupText = React.forwardRef<HTMLDivElement, ButtonGroupTextProps>(
    ({ className, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "div";
        return (
            <Comp
                ref={ref}
                className={cn(
                    "flex items-center gap-2 rounded-md border border-slate-300 bg-slate-100 px-4 text-sm font-medium text-slate-700 [&_svg]:pointer-events-none",
                    className
                )}
                {...props}
            />
        );
    }
);
ButtonGroupText.displayName = "ButtonGroupText";

/** A thin divider between buttons that share a colour and so have no border of their own. */
const ButtonGroupSeparator = React.forwardRef<
    React.ElementRef<typeof Separator>,
    React.ComponentPropsWithoutRef<typeof Separator>
>(({ className, orientation = "vertical", ...props }, ref) => (
    <Separator
        ref={ref}
        data-slot="button-group-separator"
        orientation={orientation}
        className={cn("relative !m-0 self-stretch bg-white/40 data-[orientation=vertical]:h-auto", className)}
        {...props}
    />
));
ButtonGroupSeparator.displayName = "ButtonGroupSeparator";

export { ButtonGroup, ButtonGroupSeparator, ButtonGroupText, buttonGroupVariants };
